import React, { useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import { QRCodeSVG } from "qrcode.react";
import { useSnackbar } from "notistack";

import { useWidget } from "/imports/state/WidgetContext";
import { track } from "/imports/libs/analytics";

// A signed-in player shows this to the people they play with who aren't on the
// widget yet, so they can scan it on court and sign up themselves. Phone layout
// only — on a desktop there is nobody standing next to the screen to scan it.
function InviteQrCard({ sx }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { slug, widget, saas } = useWidget();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);

  if (!slug || !isMobile) return null;

  const clubName = saas?.name || widget?.name || "the club";
  const clubUrl = `${window.location.origin}/${slug}`;
  // iPhone Safari and Android Chrome both have the share sheet; both APIs need
  // HTTPS, so a phone testing over plain http on the LAN gets no button at all —
  // the QR itself still works there.
  const canShare = typeof navigator !== "undefined" && !!navigator.share;
  const canCopy = typeof navigator !== "undefined" && !!navigator.clipboard?.writeText;

  const handleShare = async () => {
    if (canShare) {
      try {
        await navigator.share({ title: clubName, url: clubUrl });
        track("invite_link_shared", { method: "share" });
      } catch (_err) {
        // Closing the share sheet rejects too — nothing to report.
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(clubUrl);
      track("invite_link_shared", { method: "copy" });
      enqueueSnackbar("Link copied", { variant: "success" });
    } catch (_err) {
      enqueueSnackbar("Could not copy the link", { variant: "error" });
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.75,
          py: 1,
          borderRadius: 2,
          border: 1,
          borderColor: (t) => alpha(t.palette.primary.main, 0.25),
          bgcolor: "background.paper",
          ...sx
        }}
      >
        <QrCode2Icon sx={{ color: "primary.main", fontSize: "1.375rem", flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, lineHeight: 1.3 }}>Invite friends to {clubName}</Typography>
          <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.3, color: "text.secondary" }}>Show a QR code they can scan to sign up</Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={() => {
            track("invite_qr_opened");
            setOpen(true);
          }}
          sx={{
            textTransform: "none",
            flexShrink: 0,
            whiteSpace: "nowrap",
            borderRadius: 2,
            minHeight: 0,
            py: 0.375,
            px: 1.5,
            fontSize: "0.8rem",
            color: "common.white"
          }}
        >
          Show QR
        </Button>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} fullScreen>
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1, pt: "calc(0.5rem + env(safe-area-inset-top))" }}>
          <IconButton onClick={() => setOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: 2, px: 3 }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Scan to join {clubName}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Point your phone camera at the code to open {clubName} and sign up.
            </Typography>
          </Box>
          <Box
            sx={{
              width: "100%",
              maxWidth: "20rem",
              p: 2,
              borderRadius: 3,
              bgcolor: "common.white",
              border: 1,
              borderColor: "divider",
              "& svg": { display: "block", width: "100%", height: "auto" }
            }}
          >
            <QRCodeSVG value={clubUrl} size={320} level="M" marginSize={0} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
            {clubUrl.replace(/^https?:\/\//, "")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", gap: 1, px: 3, pb: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
          {(canShare || canCopy) && (
            <Button variant="contained" fullWidth onClick={handleShare} sx={{ color: "common.white" }}>
              {canShare ? "Share link" : "Copy link"}
            </Button>
          )}
          <Button variant="text" fullWidth onClick={() => setOpen(false)} sx={{ ml: "0 !important" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default InviteQrCard;
